import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * The quick favorite toggle on a drill-down facility (card + drill header) — a compact ghost star pinned top-right, on
 * the facility NAME's line (a fixed column, scannable). Gold + filled when favorited (`fill-attention`, matching the
 * classic tree), muted outline otherwise. A `role`-less button with `aria-pressed`; the kebab carries the same action
 * (both feed the one `favorites` set), so this is a convenience, not the only path.
 */
export function FavoriteStarButton({
  name,
  isFavorite,
  onToggle,
  className,
}: {
  name: string;
  isFavorite: boolean;
  onToggle: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <TooltipTrigger delay={300}>
      <Button
        variant="ghost"
        size="icon-xs"
        className={cn("rounded-full", className)}
        aria-label={isFavorite ? t("facilitySelection.nav.unfavourite", { name }) : t("facilitySelection.nav.favourite", { name })}
        aria-pressed={isFavorite}
        onPress={onToggle}
      >
        <Star className={cn("size-3.5", isFavorite ? "fill-attention text-attention" : "text-muted-foreground")} />
      </Button>
      <Tooltip>{isFavorite ? t("facilitySelection.nav.removeFavourite") : t("facilitySelection.nav.addFavourite")}</Tooltip>
    </TooltipTrigger>
  );
}
